<template>
  <!-- Login / printable / standalone pages render without the shell -->
  <router-view v-if="$route.name === 'login' || $route.meta.standalone" />

  <!-- Authenticated shell -->
  <div v-else class="app-shell">
    <AppSidebar
      :route="currentRoute"
      :pending-tour="pendingTour"
      :current-user="user"
      :open="sidebarOpen"
      @navigate="navigateTo"
      @logout="handleLogout"
      @close="sidebarOpen = false"
    />
    <div class="app-main">
      <AppHeader
        :title="pageTitle"
        :breadcrumb="[]"
        :pending-tour="pendingTour"
        :current-user="user"
        @toggle-sidebar="sidebarOpen = !sidebarOpen"
        @logout="handleLogout"
      />
      <div class="app-content">
        <!-- Keep visited pages alive so returning to a nav item is instant
             (no re-mount / re-fetch). Views refresh quietly on activated(). -->
        <router-view v-slot="{ Component }">
          <keep-alive :max="12">
            <component :is="Component" @navigate="navigateTo" />
          </keep-alive>
        </router-view>
      </div>
    </div>

    <ToastHost />

    <!-- Idle-timeout warning: shown shortly before auto sign-out -->
    <AppModal :open="showIdleWarning" title="Still there?" @close="stayActive">
      <div style="display:flex;flex-direction:column;gap:8px">
        <p style="margin:0">You've been inactive for a while. For security, you'll be signed out automatically in:</p>
        <p style="margin:0;font-size:28px;font-weight:700;color:var(--green-800);font-variant-numeric:tabular-nums">{{ idleCountdown }}s</p>
      </div>
      <template #footer>
        <AppButton @click="handleLogout">Sign out now</AppButton>
        <AppButton variant="primary" @click="stayActive">Stay signed in</AppButton>
      </template>
    </AppModal>
  </div>
</template>

<script>
import { AppSidebar, AppHeader } from '@/components/layout'
import ToastHost from '@/components/ui/ToastHost.vue'
import AppModal from '@/components/ui/AppModal.vue'
import AppButton from '@/components/ui/AppButton.vue'
import { logout } from '@/api/auth'
import { getUser } from '@/api/client'
import { getStats } from '@/api/dashboard'

const IDLE_TIMEOUT = 10 * 60 * 1000 // 10 minutes until sign-out
const WARN_BEFORE  = 60 * 1000      // warn 60s before sign-out
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart']

export default {
  name: 'App',
  components: { AppSidebar, AppHeader, ToastHost, AppModal, AppButton },
  data() {
    return {
      pendingTour: 0,
      idleTimer: null,
      warnTimer: null,
      countdownTimer: null,
      showIdleWarning: false,
      idleCountdown: 0,
      sidebarOpen: false
    }
  },
  computed: {
    currentRoute() {
      return this.$route.name || 'dashboard'
    },
    user() {
      return getUser() || { name: 'Admin', email: '', role: 'admin' }
    },
    pageTitle() {
      const titles = {
        dashboard: 'Dashboard',
        summit: 'Summit Registrations',
        tour: 'Tour Registrations',
        sponsorship: 'Sponsorships',
        attendance: 'Attendance',
        countdown: 'Countdown Campaign',
        logs: 'Activity Log',
        settings: 'Settings'
      }
      return titles[this.currentRoute] || ''
    }
  },
  created() {
    if (this.$route.name !== 'login' && !this.$route.meta.standalone) {
      this.fetchPendingTour()
      this.startIdleTimer()
    }
  },
  beforeUnmount() {
    this.stopIdleTimer()
  },
  watch: {
    '$route'(to) {
      this.sidebarOpen = false
      if (to.name === 'login' || to.meta.standalone) {
        this.stopIdleTimer()
      } else {
        this.fetchPendingTour()
        this.startIdleTimer()
      }
    }
  },
  methods: {
    navigateTo(route) {
      this.sidebarOpen = false
      this.$router.push(`/${route}`)
    },
    async handleLogout() {
      // Tear down the idle warning/timers immediately so the modal can never
      // linger (e.g. a throttled background tab firing this late).
      this.stopIdleTimer()
      try { await logout() } catch (e) { /* ignore */ }
      this.$router.push('/login')
    },
    resetIdleTimer() {
      // Ignore activity while the warning is up — it must be dismissed explicitly.
      if (this.showIdleWarning) return
      clearTimeout(this.idleTimer)
      clearTimeout(this.warnTimer)
      this.warnTimer = setTimeout(() => this.showIdleWarn(), IDLE_TIMEOUT - WARN_BEFORE)
      this.idleTimer = setTimeout(() => this.handleLogout(), IDLE_TIMEOUT)
    },
    showIdleWarn() {
      this.showIdleWarning = true
      this.idleCountdown = Math.round(WARN_BEFORE / 1000)
      clearInterval(this.countdownTimer)
      this.countdownTimer = setInterval(() => {
        this.idleCountdown -= 1
        if (this.idleCountdown <= 0) {
          clearInterval(this.countdownTimer)
          // Drive the sign-out from the countdown itself so the modal never
          // sits at "0s" if the separate idle timer is throttled/drifts.
          this.handleLogout()
        }
      }, 1000)
    },
    stayActive() {
      this.showIdleWarning = false
      clearInterval(this.countdownTimer)
      this.resetIdleTimer()
    },
    startIdleTimer() {
      this._onActivity = () => this.resetIdleTimer()
      ACTIVITY_EVENTS.forEach(e => document.addEventListener(e, this._onActivity, { passive: true }))
      this.resetIdleTimer()
    },
    stopIdleTimer() {
      clearTimeout(this.idleTimer)
      clearTimeout(this.warnTimer)
      clearInterval(this.countdownTimer)
      this.showIdleWarning = false
      if (this._onActivity) {
        ACTIVITY_EVENTS.forEach(e => document.removeEventListener(e, this._onActivity))
        this._onActivity = null
      }
    },
    async fetchPendingTour() {
      try {
        const resp = await getStats()
        if (resp.success) {
          this.pendingTour = resp.pendingTour || 0
        }
      } catch (e) {
        // silently fail — sidebar badge is non-critical
      }
    }
  }
}
</script>
